import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ClipboardList, Check, ShieldCheck, Send, X } from "lucide-react";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Btn, Loading, Empty, handleErr } from "./AdminUI";
import { useAuth } from "@/context/AuthContext";

const STATUS = {
  draft: { label: "Draft", cls: "bg-slate-500/15 text-slate-300" },
  pending_verify: { label: "Awaiting verification", cls: "bg-amber-500/15 text-amber-400" },
  pending_approve: { label: "Awaiting approval", cls: "bg-sky-500/15 text-sky-400" },
  rejected: { label: "Rejected", cls: "bg-red-500/15 text-red-400" },
  approved: { label: "Published", cls: "bg-emerald-500/15 text-emerald-400" },
};
const MODS = ["news", "shows", "events", "voting"];

export default function AdminApprovals() {
  const { can, isMainAdmin } = useAuth();
  const [data, setData] = useState({ items: {}, counts: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const r = await endpoints.adminWorkflowPending(); setData(r.data || { items: {}, counts: {} }); }
    catch (e) { handleErr(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (module, id, action) => {
    let body = {};
    if (action === "reject") { const reason = window.prompt("Reason for rejection (sent to the team):"); if (reason === null) return; body = { reason }; }
    try { await endpoints.adminWorkflowAction(module, id, action, body); toast.success(`Item ${action === "approve" ? "published" : action + "ed"}`); load(); }
    catch (e) { handleErr(e); }
  };

  const buttonsFor = (mod, item) => {
    const s = item.approvalStatus;
    const b = [];
    if ((s === "draft" || s === "rejected") && (isMainAdmin || can(mod, "create") || can(mod, "edit")))
      b.push(<Btn key="s" onClick={() => act(mod, item.id, "submit")} data-testid={`submit-${item.id}`}><Send className="w-4 h-4" /> Submit</Btn>);
    if (s === "pending_verify" && (isMainAdmin || can(mod, "verify")))
      b.push(<Btn key="v" onClick={() => act(mod, item.id, "verify")} data-testid={`verify-${item.id}`}><Check className="w-4 h-4" /> Verify</Btn>);
    if (s === "pending_approve" && (isMainAdmin || can(mod, "approve")))
      b.push(<Btn key="a" onClick={() => act(mod, item.id, "approve")} data-testid={`approve-${item.id}`}><ShieldCheck className="w-4 h-4" /> Approve & Publish</Btn>);
    if ((s === "pending_verify" || s === "pending_approve") && (isMainAdmin || can(mod, "verify") || can(mod, "approve")))
      b.push(<Btn key="r" variant="danger" onClick={() => act(mod, item.id, "reject")} data-testid={`reject-${item.id}`}><X className="w-4 h-4" /> Reject</Btn>);
    return b;
  };

  const allItems = MODS.flatMap((m) => (data.items[m] || []).map((it) => ({ ...it, module: m })));

  return (
    <div>
      <PageHeader title="Approvals" desc={`${data.total || 0} item(s) awaiting action across News, Shows, Events and Voting`} />
      {loading ? <Loading /> : allItems.length === 0 ? <Empty text="Nothing awaiting approval. All caught up! 🎉" /> : (
        <div className="grid gap-3">
          {allItems.map((item) => {
            const st = STATUS[item.approvalStatus] || STATUS.draft;
            const btns = buttonsFor(item.module, item);
            return (
              <Card key={`${item.module}-${item.id}`} data-testid={`approval-${item.id}`} className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#E11D2A]/15 text-[#E11D2A] font-bold">{item.module}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${st.cls}`}>{st.label}</span>
                  </div>
                  <div className="text-white font-semibold mt-1 truncate">{item.title}</div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {item.createdBy && <>Created by {item.createdBy.name}. </>}
                    {item.verifiedBy && <>Verified by {item.verifiedBy.name}. </>}
                    {item.rejectionReason && item.approvalStatus === "rejected" && <span className="text-red-400">Rejected: {item.rejectionReason}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">{btns.length ? btns : <span className="text-xs text-slate-500">No action available</span>}</div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
